import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { updateClaims } from '../../../utils/authStore';
import { graphql } from 'client/gql';
import { useQuery } from 'urql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { CooperationCoursesTable } from 'components/Courses/tables/CooperationCoursesTable';
import { GroupType } from '@bp/bp-graphql-types';
import { showUserDbErrorToast } from 'utils/toasts';
import { CourseMembersModal } from 'components/Courses/modals/CourseMembersModal';

const SettingsCollaborationCoursesSubpageQueryDocument = graphql(`
  query SettingsCollaborationCoursesSubpageData($courseWhere: GroupWhere!, $profile: ID!) {
    ...PermissionCheckerFields
    ...UseCoursesFields
  }
`);

export const SettingsCollaborationCoursesSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const navigate = useNavigate();

  const [claimsTrigger, setClaimsTrigger] = useState<boolean>(false);
  const [membersModalOpen, setMembersModalOpen] = useState<boolean>(false);
  const [courseUuid, setCourseUuid] = useState<string | null>(null);

  const [{ data, error }] = useQuery({
    query: SettingsCollaborationCoursesSubpageQueryDocument,
    variables: {
      profile: profileUuid,
      courseWhere: {
        type: GroupType.Course,
        isCommunity: true,
        AND: [
          {
            organization_NOT: { uuid: organizationUuid },
          },
          {
            admins_SOME: { organization: { uuid: organizationUuid } },
          },
        ],
      },
    },
  });

  if (error) showUserDbErrorToast();

  const permissions = usePermissionChecker(data);

  function handleNavigate(uuid: string) {
    navigate('/courses/' + uuid);
  }

  function handleProfilesEdit(uuid: string) {
    setCourseUuid(uuid);
    setMembersModalOpen(true);
  }

  async function handleClose() {
    await updateClaims(() => setClaimsTrigger(!claimsTrigger));
    setCourseUuid(null);
    setMembersModalOpen(false);
  }

  return (
    <BpSubpage isForbidden={!permissions?.canUpdateCollaborationCourses() || !permissions?.canCreateCourse()}>
      <CooperationCoursesTable
        query={data}
        permissions={permissions}
        onNavigate={handleNavigate}
        onProfilesEdit={handleProfilesEdit}
      />

      {courseUuid && <CourseMembersModal isOpen={membersModalOpen} courseUuid={courseUuid} onClose={handleClose} />}
    </BpSubpage>
  );
};
